// Agent Manager - Central service for all agents
// frontend/src/services/agentManager.ts

import { AgentCommunicationHub } from '../agents/core/AgentCommunicationHub';
import { CostTrackingAgent } from '../agents/specialized/CostTrackingAgent';
import { TradeOffAgent } from '../agents/specialized/TradeOffAgent';
import { ApprovalWorkflowAgent } from '../agents/specialized/ApprovalWorkflowAgent';

class AgentManager {
    private static instance: AgentManager;

    hub: AgentCommunicationHub;
    costAgent?: CostTrackingAgent;
    tradeOffAgent?: TradeOffAgent;
    approvalAgent?: ApprovalWorkflowAgent;

    private constructor() {
        this.hub = new AgentCommunicationHub();
        console.log('[AgentManager] Instance created');
    }

    static getInstance(): AgentManager {
        if (!AgentManager.instance) {
            AgentManager.instance = new AgentManager();
        }
        return AgentManager.instance;
    }

    /**
     * Initialize agents for a new epoch
     */
    initializeForEpoch(budget: number): void {
        console.log(`[AgentManager] Initializing agents for epoch with budget $${budget.toFixed(2)}...`);

        // Create or reset cost agent
        if (this.costAgent) {
            this.costAgent.setBudget(budget);
            this.costAgent.reset();
        } else {
            this.costAgent = new CostTrackingAgent(budget, this.hub);
        }

        // Create trade-off agent if not exists
        if (!this.tradeOffAgent) {
            this.tradeOffAgent = new TradeOffAgent(this.hub);
        }

        // Create approval agent if not exists
        if (!this.approvalAgent) {
            this.approvalAgent = new ApprovalWorkflowAgent(this.hub);
        }

        console.log('[AgentManager] ✅ Agents ready!');
        console.log(`[AgentManager] Registered agents: ${this.hub.getRegisteredAgents().join(', ')}`);
    }

    /**
     * Get cost tracking agent
     */
    getCostAgent(): CostTrackingAgent {
        if (!this.costAgent) {
            throw new Error('Agents not initialized. Call initializeForEpoch() first.');
        }
        return this.costAgent;
    }

    /**
     * Get trade-off optimization agent
     */
    getTradeOffAgent(): TradeOffAgent {
        if (!this.tradeOffAgent) {
            throw new Error('Agents not initialized. Call initializeForEpoch() first.');
        }
        return this.tradeOffAgent;
    }

    /**
     * Get approval workflow agent
     */
    getApprovalAgent(): ApprovalWorkflowAgent {
        if (!this.approvalAgent) {
            throw new Error('Agents not initialized. Call initializeForEpoch() first.');
        }
        return this.approvalAgent;
    }

    /**
     * Get communication hub
     */
    getHub(): AgentCommunicationHub {
        return this.hub;
    }

    /**
     * Reset all agents
     */
    reset(): void {
        this.costAgent?.reset();
        console.log('[AgentManager] Agents reset');
    }

    /**
     * Get message history statistics
     */
    getStatistics() {
        return this.hub.getStatistics();
    }

    /**
     * Check if agents are initialized
     */
    isInitialized(): boolean {
        return !!this.costAgent && !!this.tradeOffAgent && !!this.approvalAgent;
    }
}

// Export singleton instance
export const agentManager = AgentManager.getInstance();
